<template>
  <div class="content">
    <h3>Gestion des Appels</h3>
    <!-- Filters Section -->
    <div class="card">
      <div class="card-body">
        <div class="row">
          <base-input class="col-md-3">
            <el-date-picker
              v-model="filters.date_start"
              value-format="yyyy-MM-dd"
              type="date"
              placeholder="Date de début"
            ></el-date-picker>
          </base-input>
          <base-input class="col-md-3">
            <el-date-picker
              v-model="filters.date_end"
              value-format="yyyy-MM-dd"
              type="date"
              placeholder="Date de fin"
            ></el-date-picker>
          </base-input>
          <base-input class="col-md-3">
            <el-input
              v-model="filters.portal_name"
              placeholder="Nom du portail"
              clearable
            ></el-input>
          </base-input>
          <base-input class="col-md-3">
            <el-input
              v-model="filters.filtre"
              placeholder="Filtrer par ID/destination/extension/disposition"
              clearable
            ></el-input>
          </base-input>
        </div>
        <div class="col-xs-4 text-right mt-3">
          <button
            class="btn btn-market mr-2"
            @click="DownloadCSV"
          >
            <i class="tim-icons icon-cloud-download-93"></i> EXPORT CSV
          </button>
          <base-button
            v-if="loading"
            loading
            class="btn btn-default"
          >
            Chargement...
          </base-button>
          <button
            v-else
            class="btn btn-default"
            @click="fetchCallsList"
          >
            Rechercher
          </button>
        </div>
      </div>
    </div>

    <card>
      <div class="row">
        <div class="col-xl-1 col-sm-6">
          <base-input>
            <el-select
              class="select-primary pagination-select"
              v-model="pagination.perPage"
              placeholder="Par page"
            >
              <el-option
                class="select-primary"
                v-for="item in pagination.perPageOptions"
                :key="item"
                :label="item"
                :value="item"
              >
              </el-option>
            </el-select>
          </base-input>
        </div>
        <div class="col-xl-8 col-sm-6">
          <label style="margin-top: 10px; font-size: 0.75rem">Lignes par page</label>
        </div>
        <base-input class="col-xl-3 col-sm-6">
          <el-input
            type="search"
            class="mb-3 search-input"
            clearable
            prefix-icon="el-icon-search"
            placeholder="Recherche"
            v-model="searchQuery"
          ></el-input>
        </base-input>
      </div>

      <el-table
        :data="paginatedCallsList"
        v-loading="loading"
        row-class-name="calls-table-rows"
        class="customtable"
        style="width: 100%;"
      >
        <!-- ID Column -->
        <el-table-column prop="id" label="ID" width="100" sortable>
          <template #default="{ row }">
            <el-tooltip :content="row.id.toString()" :open-delay="300" placement="top">
              <span>{{ row.id }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <!-- Call Date -->
        <el-table-column prop="calldate" label="Date d'appel" width="150" sortable>
          <template #default="{ row }">
            <el-tooltip :content="new Date(row.calldate).toLocaleString()" :open-delay="300" placement="top">
              <span>{{ new Date(row.calldate).toLocaleString() }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <!-- Caller ID -->
        <el-table-column prop="callerid" label="Appelant" width="140" sortable>
          <template #default="{ row }">
            <el-tooltip :content="row.callerid" :open-delay="300" placement="top">
              <span>0{{ row.callerid }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <!-- Destination -->
        <el-table-column prop="destination" label="Destination" width="120" sortable>
          <template #default="{ row }">
            <el-tooltip :content="row.destination" :open-delay="300" placement="top">
              <span>{{ row.destination }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <!-- Extension -->
        <el-table-column prop="extension" label="Extension" width="100" sortable>
          <template #default="{ row }">
            <el-tooltip :content="row.extension" :open-delay="300" placement="top">
              <span>{{ row.extension }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <!-- Extension Name -->
        <el-table-column prop="extension_name" label="Nom Extension" width="150" sortable>
          <template #default="{ row }">
            <el-tooltip :content="row.extension_name" :open-delay="300" placement="top">
              <span>{{ row.extension_name }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <!-- Disposition -->
        <el-table-column prop="disposition" label="Disposition" width="120" sortable>
          <template #default="{ row }">
            <el-tooltip :content="row.disposition" :open-delay="300" placement="top">
              <span class="disposition-badge" :class="getDispositionClass(row.disposition)">
                {{ row.disposition }}
              </span>
            </el-tooltip>
          </template>
        </el-table-column>

        <!-- Duration -->
        <el-table-column prop="duration" label="Durée (s)" width="100" sortable>
          <template #default="{ row }">
            <el-tooltip :content="`${row.duration} secondes`" :open-delay="300" placement="top">
              <span>{{ formatDuration(row.duration) }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <!-- Call Type -->
        <el-table-column prop="call_type" label="Type d'appel" width="120" sortable>
          <template #default="{ row }">
            <el-tooltip :content="row.call_type" :open-delay="300" placement="top">
              <span>{{ row.call_type }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <!-- Portal Name -->
        <el-table-column prop="portal_name" label="Portail" width="150" sortable>
          <template #default="{ row }">
            <el-tooltip :content="row.portal_name" :open-delay="300" placement="top">
              <span>{{ row.portal_name }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <!-- Portal Number -->
        <el-table-column prop="portal_number" label="Numéro Portail" width="130" sortable>
          <template #default="{ row }">
            <el-tooltip :content="row.portal_number" :open-delay="300" placement="top">
              <span>{{ row.portal_number }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <!-- Rate -->
        <el-table-column prop="rate" label="Tarif" width="100" sortable>
          <template #default="{ row }">
            <el-tooltip :content="`${row.rate} €`" :open-delay="300" placement="top">
              <span>{{ parseFloat(row.rate).toFixed(4) }} €</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <!-- Call Cost -->
        <el-table-column prop="call_cost" label="Coût" width="100" sortable>
          <template #default="{ row }">
            <el-tooltip :content="`${row.call_cost} €`" :open-delay="300" placement="top">
              <span>{{ parseFloat(row.call_cost).toFixed(4) }} €</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <!-- Voicemail Duration -->
        <el-table-column prop="voicemail_duration" label="Durée VM" width="100" sortable>
          <template #default="{ row }">
            <el-tooltip :content="`${row.voicemail_duration} secondes`" :open-delay="300" placement="top">
              <span>{{ row.voicemail_duration > 0 ? formatDuration(row.voicemail_duration) : '-' }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <!-- Created At -->
        <el-table-column prop="created_at" label="Créé le" width="150" sortable>
          <template #default="{ row }">
            <el-tooltip :content="new Date(row.created_at).toLocaleString()" :open-delay="300" placement="top">
              <span>{{ new Date(row.created_at).toLocaleString() }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>

      <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
        <div>
          <p class="card-category">
            Affichage de {{ from + 1 }} à {{ to }} sur {{ total }} entrées
          </p>
        </div>
        <base-pagination
          type="warning"
          class="pagination-no-border"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total="total"
        ></base-pagination>
      </div>
    </card>
  </div>
</template>

<script>
import axios from "axios";
import { Table, TableColumn, Select, Option, Input, DatePicker } from "element-ui";
import { BaseButton, BasePagination } from "src/components";
import XLSX from 'xlsx';

export default {
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Input.name]: Input,
    BaseButton,
    [DatePicker.name]: DatePicker,
    BasePagination,
  },
  data() {
    const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD

    return {
      callsList: [],
      loading: false,
      filters: {
        date_start: today,
        date_end: today,
        portal_name: '',
        filtre: '',
      },
      searchQuery: "",
      pagination: {
        perPage: 50,
        currentPage: 1,
        perPageOptions: [50, 100, 250, 500],
      },
    };
  },
  computed: {
    filteredCallsList() {
      if (!this.searchQuery) return this.callsList;
      return this.callsList.filter((item) =>
        JSON.stringify(item).toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
    paginatedCallsList() {
      const start = this.from;
      const end = this.to;
      return this.filteredCallsList.slice(start, end);
    },
    from() {
      return (this.pagination.currentPage - 1) * this.pagination.perPage;
    },
    to() {
      return Math.min(this.pagination.currentPage * this.pagination.perPage, this.total);
    },
    total() {
      return this.filteredCallsList.length;
    },
  },
  methods: {
    async fetchCallsList() {
      this.loading = true;
      let accessToken = localStorage.getItem("token_jwt");
      const apiUrl = "https://apimyaffil.wee-do-it.net/api/manivox/listCalls";

      try {
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${accessToken}`
          },
          params: this.filters
        });

         this.callsList = response.data.response.result;
      } catch (error) {
        console.error("Error fetching calls list:", error);
        this.$notify({
          type: "danger",
          message: "Échec de la récupération des données. Veuillez réessayer.",
        });
      } finally {
        this.loading = false;
      }
    },

    DownloadCSV() {
      // Helper function to get day name in French
      const getDayName = (date) => {
        const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
        return days[date.getDay()];
      };

      // Helper function to format time
      const formatTime = (date) => {
        return date.toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      };

      // Create data that matches exactly what's displayed in the table
      const csvData = this.callsList.map(row => {
        const callDate = new Date(row.calldate);

        return {
          'ID': row.id,
          'Date d\'appel': callDate.toLocaleString(),
          'Jour d\'appel': getDayName(callDate),
          'Heure d\'appel': formatTime(callDate),
          'Appelant': `0${row.callerid}`,
          'Destination': row.destination,
          'Extension': row.extension,
          'Nom Extension': row.extension_name,
          'Disposition': row.disposition,
          'Durée (s)': this.formatDuration(row.duration),
          'Type d\'appel': row.call_type,
          'Portail': row.portal_name,
          'Numéro Portail': row.portal_number,
          'Tarif': `${parseFloat(row.rate).toFixed(4)} €`,
          'Coût': `${parseFloat(row.call_cost).toFixed(4)} €`,
          'Durée VM': row.voicemail_duration > 0 ? this.formatDuration(row.voicemail_duration) : '-',
          'Créé le': new Date(row.created_at).toLocaleString()
        };
      });

      const fileName = `Gestion_des_appels_${new Date().toISOString().split('T')[0]}.csv`;

      // Convert to CSV
      const worksheet = XLSX.utils.json_to_sheet(csvData);
      const csvContent = XLSX.utils.sheet_to_csv(worksheet, { FS: ";" });

      // Create and download the file with BOM for proper encoding
      const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    getDispositionClass(disposition) {
      switch (disposition) {
        case 'ANSWERED':
          return 'disposition-answered';
        case 'BUSY':
          return 'disposition-busy';
        case 'NO ANSWER':
          return 'disposition-no-answer';
        case 'FAILED':
          return 'disposition-failed';
        default:
          return 'disposition-default';
      }
    },
    formatDuration(seconds) {
      if (seconds === 0) return '0s';
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
    }
  },
  mounted() {
    this.fetchCallsList();
  },
};
</script>

<style scoped>
.content {
  padding: 20px;
}

.calls-table-rows {
  text-transform: capitalize;
}

.customtable {
  word-break: break-word;
}

.disposition-badge {
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.75rem;
  font-weight: bold;
}

.disposition-answered {
  background-color: #d4edda;
  color: #155724;
}

.disposition-busy {
  background-color: #fff3cd;
  color: #856404;
}

.disposition-no-answer {
  background-color: #f8d7da;
  color: #721c24;
}

.disposition-failed {
  background-color: #f5c6cb;
  color: #721c24;
}

.disposition-default {
  background-color: #e2e3e5;
  color: #383d41;
}
</style>
